<script setup lang="ts">
import type { PrimitivesValue } from '../../types/shared'
import type { RadioInjection } from './types'
import { APP_RADIO_KEY } from './constants'

type Props = {
  value: PrimitivesValue
  label?: string
  disabled?: boolean
}

const props = defineProps<Props>()

const componentClasses = computed(() => {
  const classes = ['app-radio']

  if (props.disabled) classes.push('app-radio--disabled')

  return classes
})

const isChecked = computed(() => injection?.selectedValue.value === props.value)

const onChange = () => injection?.updateValue(props.value)

const injection = inject<RadioInjection>(APP_RADIO_KEY)
</script>

<template>
  <label :class="componentClasses">
    <input
      class="app-radio__input ui-visually-hidden"
      type="radio"
      :name="injection?.name"
      :aria-label="label"
      :aria-disabled="disabled"
      :checked="isChecked"
      :disabled="disabled"
      @change="onChange"
    />

    <span class="app-radio__checkmark" />

    <span v-if="label" class="app-radio__label">{{ label }}</span>

    <slot />
  </label>
</template>

<style lang="scss" scoped>
.app-radio {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 2px;
  cursor: pointer;
  user-select: none;

  &--disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &__label {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: var(--ui-text-primary);
  }

  &__checkmark {
    position: relative;
    width: 20px;
    height: 20px;
    border: 2px solid var(--ui-secondary);
    border-radius: 50%;

    &::before,
    &::after {
      position: absolute;
      content: '';
      background-color: var(--ui-secondary);
      border-radius: inherit;
      opacity: 0;
      transition: opacity 0.1s linear;
    }

    &::before {
      top: -6px;
      right: -6px;
      bottom: -6px;
      left: -6px;
    }

    &::after {
      top: 50%;
      left: 50%;
      width: 10px;
      height: 10px;
      transform: translate(-50%, -50%);
    }

    &:hover {
      &::before {
        opacity: 0.05;
      }
    }

    &:active {
      &::before {
        opacity: 0.2;
      }
    }
  }

  &__input {
    &:focus-visible ~ .app-radio__checkmark {
      &::before {
        opacity: 0.1;
      }
    }

    &:checked ~ .app-radio__checkmark {
      &::after {
        opacity: 1;
      }
    }
  }
}
</style>
